import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { MODELING_CONST } from '@/constant';
import { useModelingRemoveDeployment, useTypedSearchParams } from '@/hook';
import type { VTableColumnFilter, VTableData } from '@/package';
import { DeveloperBoard, VFlex, VStack, VTable, useVModal } from '@/package';
import { VStatusBadge } from '@/package';
import type { Modeling, ModelingSearchParams } from '@/type';
import { bytesToMB, calculateTrainingTime } from '@/util';

import { EmptyRow } from '../EmptyRow';
import { useDeploymentContext } from './hook';

const calcTrainingProgressValue = (deployStartTime: DateLike) => {
  const now = dayjs();
  const startDate = dayjs(deployStartTime);
  const targetDuration = 15 * 60 * 1000;
  const elapsedTime = now.diff(startDate);

  if (elapsedTime >= targetDuration) {
    return 99.99;
  }

  const progress = +((elapsedTime / targetDuration) * 100).toFixed(2);

  return Math.max(0, progress);
};

const DeploymentList = () => {
  const { count, modelingList } = useDeploymentContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openDangerConfirm } = useVModal();
  const { mutate: removeDeployment } = useModelingRemoveDeployment();
  const rowRef = useRef<HTMLDivElement>(null);
  const { getAll, remove, append, flush } =
    useTypedSearchParams<ModelingSearchParams>();
  const renderedModelingList = modelingList.map(m => {
    return {
      id: m.id,
      status: m.deployStatus,
      modelName: m.name,
      versionName: m.version || '-',
      size: m.deployEngineSize,
      deploymentTime: calculateTrainingTime(m),
      deployedAt: m.deployEndTime,
      remove: null,

      raw: m,
    };
  });

  const onRemove = (modelingID: Modeling['id']) => {
    openDangerConfirm({
      title: t('modeling.removeDeploymentModalTitle.hydatidiform'),
      message() {
        return (
          <VStack gap={0.25}>
            <div>{t('modeling.removeDeploymentModalMessage.ablactate')}</div>
          </VStack>
        );
      },
      confirmText: t('modal.yes.scripless'),
      cancelText: t('modal.no.kontakion'),
      size: 'small',

      onConfirm() {
        removeDeployment({ pModelingID: modelingID });
      },
    });
  };

  const mapDeployStatusToLabel = (status: Modeling['deployStatus']) => {
    return status === 'Deploying'
      ? 'Deploying'
      : status === 'DeployPending'
        ? 'Pending'
        : status === 'DeployFailure'
          ? 'Failure'
          : status;
  };

  const statusFilterItemList = Object.values(
    MODELING_CONST.MODELING_DEPLOY_STATUS,
  ).map(status => {
    const isSelected = getAll('modelingDeployStatus').includes(status);
    return {
      value: status,
      label: mapDeployStatusToLabel(status),
      isSelected,
    };
  });

  const columnFilter = [
    {
      id: 'status',
      itemList: statusFilterItemList,
    },
  ];

  return (
    <VStack className="flex-1 overflow-y-auto pt-2" gap={0.5}>
      <VFlex className="text-body-xl text-gray-500" gap={0.5} items="center">
        <div>
          <span>{t('common.total.briming')}&nbsp;</span>
          <span>{count}&nbsp;</span>
        </div>
      </VFlex>

      <VTable
        ref={rowRef}
        data={renderedModelingList}
        className="h-full overflow-y-auto"
        columnList={[
          'id',
          'status',
          'modelName',
          'versionName',
          'size',
          'deploymentTime',
          'deployedAt',
          'remove',
        ]}
        cellRenderer={{
          status: (val, _, row) => {
            if (val === 'Deploying') {
              return (
                <VStatusBadge variant="inProgress">
                  <DeploymentProgress modeling={row.raw} />
                </VStatusBadge>
              );
            }

            if (val === 'DeployPending') {
              return <VStatusBadge variant="notStarted">Pending</VStatusBadge>;
            }

            if (val === 'DeployFailure') {
              return <VStatusBadge variant="failed">Failure</VStatusBadge>;
            }

            return <VStatusBadge variant="completed">{val}</VStatusBadge>;
          },
          deploymentTime: val => {
            const { hours, minutes, seconds } = val;

            return (
              <div>{`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`}</div>
            );
          },
          deployedAt: val => dayjs(val).format('YYYY-MM-DD HH:mm:ss'),
          size: val => `${bytesToMB(val).toFixed(2)} MB`,
        }}
        columnStyleMap={{
          id: {
            className: 'w-[64px]',
          },
          status: {
            className: 'w-[98px]',
          },
          size: {
            className: 'w-[124px]',
          },
          deploymentTime: {
            className: 'w-[127px]',
          },
          deployedAt: {
            className: 'w-[184px]',
          },
        }}
        setRowSelected={() => true}
        setRowRemovable={row => row.raw.deployStatus === 'DeployFailure'}
        renderEmptyData={() => (
          <EmptyRow
            Icon={DeveloperBoard}
            description={`No Deployments have been registered yet.
              Start by training a model in [Modeling],
              then proceed with Deployment.`}
            buttonText="Go to Modeling"
            ButtonIcon={DeveloperBoard}
            onClick={() => navigate('/modeling')}
          />
        )}
        columnFilter={columnFilter as VTableColumnFilter<VTableData>[]}
        onColumnFilterChange={columnFilter => {
          const statusColumnFilter = columnFilter.find(
            filter => filter.id === 'status',
          );
          const statusColumnFilterItemList = statusColumnFilter?.itemList;
          remove('modelingDeployStatus');
          statusColumnFilterItemList?.forEach(item => {
            item.isSelected && append('modelingDeployStatus', item.value);
          });
          flush();
        }}
        onRemove={onRemove}
        onRowClick={e => {
          navigate(`/modeling/${e._target.row.id}`);
        }}
      />
    </VStack>
  );
};

const DeploymentProgress = ({ modeling }: { modeling: Modeling }) => {
  const [_, rerender] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      rerender(prev => prev + 1);
    }, 1000);

    return () => clearInterval(id);
  }, []);
  return <div>{calcTrainingProgressValue(modeling.deployStartTime)}%</div>;
};

export default DeploymentList;
